import { createAsyncThunk } from '@reduxjs/toolkit'
import { wikiAPI } from '../../utils/supabase'

// === PAGE THUNKS ===

export const fetchPages = createAsyncThunk(
  'wiki/fetchPages',
  async (userId, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.getAllPages(userId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const fetchPageContent = createAsyncThunk(
  'wiki/fetchPageContent',
  async (pageId, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.getPageWithContent(pageId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const createPage = createAsyncThunk(
  'wiki/createPage',
  async ({ userId, pageData }, { getState, rejectWithValue }) => {
    // Calculate display_order as count of sibling pages (same space and parent)
    const state = getState()
    const existingPages = state.wiki.pages
    const siblingCount = existingPages.filter(
      p => p.space_id === pageData.space_id &&
           p.parent_page_id === pageData.parent_page_id
    ).length

    const { data, error } = await wikiAPI.createPage(userId, {
      ...pageData,
      display_order: siblingCount
    })
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const updatePage = createAsyncThunk(
  'wiki/updatePage',
  async ({ pageId, updates }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.updatePage(pageId, updates)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const deletePage = createAsyncThunk(
  'wiki/deletePage',
  async (pageId, { rejectWithValue }) => {
    const { error } = await wikiAPI.deletePage(pageId)
    if (error) return rejectWithValue(error.message)
    return pageId
  }
)

// Archive a page and cascade-archive its sub-tree (soft, reversible)
export const archivePage = createAsyncThunk(
  'wiki/archivePage',
  async ({ pageId, userId }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.archivePage(pageId, userId)
    if (error) return rejectWithValue(error.message)
    // data = { page_ids }
    return { pageIds: data?.page_ids || [] }
  }
)

// Restore an archived page and its archived sub-tree
export const restorePage = createAsyncThunk(
  'wiki/restorePage',
  async ({ pageId, userId }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.restorePage(pageId, userId)
    if (error) return rejectWithValue(error.message)
    // data = { pages }
    return { pages: data?.pages || [] }
  }
)

// Fetch archived pages metadata (on-demand for the Archived view)
export const fetchArchivedPages = createAsyncThunk(
  'wiki/fetchArchivedPages',
  async (userId, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.getArchivedPages(userId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const movePage = createAsyncThunk(
  'wiki/movePage',
  async ({ pageId, newParentId, newSpaceId }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.movePage(pageId, newParentId, newSpaceId)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const togglePageFavorite = createAsyncThunk(
  'wiki/togglePageFavorite',
  async ({ pageId, isFavorite }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.toggleFavorite(pageId, isFavorite)
    if (error) return rejectWithValue(error.message)
    return data
  }
)

export const updatePageOrder = createAsyncThunk(
  'wiki/updatePageOrder',
  async ({ pages }, { rejectWithValue }) => {
    const { data, error } = await wikiAPI.updatePageOrder(pages)
    if (error) return rejectWithValue(error.message)
    return data
  }
)
